import { useState, useEffect, useLayoutEffect, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { IconChevronDown, IconFileCv, IconPhotoEdit, IconUsersGroup } from "@tabler/icons-react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useTranslation } from "../../i18n/hooks/useTranslation";
import { cn } from "@/lib/utils";
import aiTemplateCategoryService from "../../services/aiTemplateCategoryService";
import printTemplateCategoryService from "../../services/printTemplateCategoryService";
import aiTemplateService from "../../services/aiTemplateService";
import { useAiTemplateCacheStore } from "../../store/aiTemplateCacheStore";
import { useToolUsageStore } from "../../store/toolUsageStore";
import { useAuthStore } from "../../store/authStore";
import AiTemplateMegaMenu from "./AiTemplateMegaMenu";
import megamenuData from "../../../megamenu.json";
import { ICON_OVERRIDES, isInternalTool, SITE_URL, toSlug } from "../../pages/user/tools/toolCatalog";

// Tools whose own icon image belongs to another palette (grey, or a different
// column's colour). In this menu they get a vector that takes their column's hue.
const MENU_RECOLOR_ICONS = {
    "general/family-card-form": IconUsersGroup,
    "general/ats-cv-maker": IconFileCv,
    "image/govt-job-photo-sign": IconPhotoEdit,
};

const MAX_DISPLAY = 6;
// A category can show fewer tools than the default (the rest fall under "+N more").
const MAX_DISPLAY_BY_CAT = { result: 5 };

// Tools pinned to the front / back of their category's dropdown column, ignoring usage order.
// Educational leads with Form auto fillup and keeps Education Board Result last (under "+ more").
const PINNED_FIRST = { result: '/tools/form-auto-fillup' };
const PINNED_LAST = { result: '/tools/result/education-board-result' };
const TOOL_COUNT = megamenuData.categories.reduce((n, c) => n + c.tools.length, 0);
// JSON paths use /tools-icon/<name>.svg; the files live in R2 at
// files.sohozkaj.com/icons/<name>.svg. Per-tool folders keep their name
// except "general/", which is "general-tools/" on R2.
const toolIconSrc = (path) =>
    !path
        ? ""
        : `https://files.sohozkaj.com/icons/${path.replace(/^\/tools-icon\//, "").replace(/^general\//, "general-tools/")}`;
// Shared shell of the full-width mega panels (Tools + AI templates).
const MEGA_SHELL_CLASS = "-mt-1 bg-white dark:bg-zinc-950 border border-zinc-200 dark:border-zinc-800 rounded-2xl shadow-2xl p-5";
// The AI-templates panel is two short lists side by side, so it hugs its content (centered under the
// header, tighter padding) instead of spanning the container with empty space on both sides.
const AI_SHELL_CLASS = cn(MEGA_SHELL_CLASS, "mx-auto max-w-[1040px] p-4");
const fmtBn = (n) => String(n).replace(/[0-9]/g, (d) => '০১২৩৪৫৬৭৮৯'[d]);

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

/** A single pill-style tab button. Active = soft brand tint + brand-colored icon. */
function TabButton({ active, onClick, children, className }) {
    const { i18n } = useTranslation();
    // English uses a slightly larger, fixed 15px menu font; Bangla keeps the compact sizes.
    const sizeCls = (i18n.language || "").startsWith("en")
        ? "text-[15px]"
        : "text-[12.5px] min-[1100px]:text-[13.5px]";
    return (
        <button
            type="button"
            onClick={onClick}
            className={cn(
                "relative flex items-center gap-1 min-[1100px]:gap-1.5 whitespace-nowrap font-bold",
                sizeCls,
                "px-1.5 min-[1100px]:px-2.5 py-2 rounded-lg transition-colors duration-150 focus:outline-none",
                active
                    ? "bg-primary/10 text-zinc-900 dark:bg-primary/20 dark:text-zinc-100 font-semibold [&_svg]:text-primary"
                    : "text-[#444] dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-200 hover:bg-zinc-100 dark:hover:bg-zinc-800",
                className
            )}
        >
            {children}
        </button>
    );
}

/** Small inline badge used on nav tabs (count). */
function NavBadge({ tone = 'orange', children }) {
    return (
        <span className={cn(
            "text-[9px] font-bold px-1.5 py-0.5 rounded leading-none",
            tone === 'blue'
                ? "text-blue-600 bg-blue-50 dark:text-blue-400 dark:bg-blue-900/30"
                : "text-primary bg-primary/10 dark:text-primary-300 dark:bg-primary/20"
        )}>
            {children}
        </span>
    );
}

/** Floating "নতুন" badge — sits above the tab's top edge so it doesn't eat inline width. */
function FloatingBadge({ children, tone = 'orange' }) {
    return (
        <span className={cn(
            "absolute -top-1.5 right-0 z-20 pointer-events-none text-[8.5px] font-bold px-1.5 py-0.5 rounded-full leading-none text-white shadow-sm",
            tone === 'green' ? "bg-emerald-500" : "bg-primary"
        )}>
            {children}
        </span>
    );
}

/** Hover-triggered dropdown wrapper.
 *  Renders a trigger + absolutely-positioned dropdown that stays open
 *  while the pointer is anywhere inside the combined area. */
function HoverDropdown({ trigger, children, dropdownClassName, alignLeft = true, className }) {
    const [open, setOpen] = useState(false);
    const timeoutRef = useRef(null);

    const show = () => {
        clearTimeout(timeoutRef.current);
        setOpen(true);
    };

    const hide = () => {
        // Small delay so mouse can move from trigger into the dropdown panel
        timeoutRef.current = setTimeout(() => setOpen(false), 120);
    };

    return (
        <div
            className={cn("relative inline-flex", className)}
            onMouseEnter={show}
            onMouseLeave={hide}
        >
            {trigger}
            {open && (
                <div
                    className={cn(
                        "absolute top-full z-[200] mt-0.5",
                        alignLeft ? "left-0" : "right-0",
                        "bg-white dark:bg-zinc-900",
                        "border border-zinc-200 dark:border-zinc-700",
                        "rounded-lg shadow-lg",
                        dropdownClassName
                    )}
                    onMouseEnter={show}
                    onMouseLeave={hide}
                >
                    {children}
                </div>
            )}
        </div>
    );
}

// ---------------------------------------------------------------------------
// PageMenu
// ---------------------------------------------------------------------------

export default function PageMenu({ menuItems = [], trailing = null, fitHidden = null, onFitChange = null }) {
    const navigate = useNavigate();
    const location = useLocation();
    const { t, i18n } = useTranslation();
    const [aiCategories, setAiCategories] = useState([]);
    const [printCategories, setPrintCategories] = useState([]);
    const [megaOpen, setMegaOpen] = useState(false);
    const megaTimeoutRef = useRef(null);
    // AI-templates mega panel: opens on hover of the AI tab; its template list is loaded
    // lazily on first open (shared 12h cache with the gallery) so the navbar costs nothing
    // until someone actually hovers it.
    const [aiMegaOpen, setAiMegaOpen] = useState(false);
    const aiMegaTimeoutRef = useRef(null);
    const [aiTemplates, setAiTemplates] = useState([]);
    const [aiTemplatesLoading, setAiTemplatesLoading] = useState(false);
    const aiTemplatesRequestedRef = useRef(false);
    // Full-width slot (rendered in the navbar row) the megamenu portals into so it
    // spans the whole container instead of just this nav's middle column.
    const [megaSlot, setMegaSlot] = useState(null);
    useEffect(() => {
        setMegaSlot(document.getElementById("sohoz-megamenu-slot"));
    }, []);
    const [isDark, setIsDark] = useState(() =>
        document.documentElement.classList.contains("dark")
    );
    useEffect(() => {
        const obs = new MutationObserver(() =>
            setIsDark(document.documentElement.classList.contains("dark"))
        );
        obs.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
        return () => obs.disconnect();
    }, []);

    // Tools mega panel: inside every category the tools are ordered by how often EVERYONE has run them —
    // all-time totals across all users, the same numbers the tool pages show as "used N times" (not this
    // user's own history). Ties and tools nobody has run yet keep the catalogue order. The category
    // columns themselves never move.
    const hasToolsMega = menuItems.some((item) => item.megamenu);
    const token = useAuthStore((s) => s.token);
    const usageTotals = useToolUsageStore((s) => s.totals);
    const loadToolTotals = useToolUsageStore((s) => s.loadTotals);
    useEffect(() => {
        if (hasToolsMega && token) loadToolTotals();
    }, [hasToolsMega, token, loadToolTotals]);
    const megaCategories = useMemo(() => {
        const totals = token ? usageTotals : {};
        return megamenuData.categories.map((category) => {
            let tools = [...category.tools].sort((a, b) => (totals[toSlug(b.path)] || 0) - (totals[toSlug(a.path)] || 0));
            // Pin one tool to the front and/or back of the column, regardless of how much each is
            // used — Form auto fillup leads the Educational column and Education Board Result trails it.
            const first = PINNED_FIRST[category.id];
            if (first) tools = [...tools.filter((x) => x.path === first), ...tools.filter((x) => x.path !== first)];
            const last = PINNED_LAST[category.id];
            if (last) tools = [...tools.filter((x) => x.path !== last), ...tools.filter((x) => x.path === last)];
            return { ...category, tools };
        });
    }, [usageTotals, token]);

    // A tab can hang its module's categories off itself — AI templates and print
    // templates each have their own table, so only the lists actually on screen
    // are fetched.
    const needsAiCategories = menuItems.some((item) => item.categoryDropdown === "ai" || item.categoryDropdown === true);
    const needsPrintCategories = menuItems.some((item) => item.categoryDropdown === "print");
    useEffect(() => {
        if (!needsAiCategories) return;
        aiTemplateCategoryService
            .list()
            .then((r) => setAiCategories((r?.data || []).filter((c) => c.status === "published")))
            .catch(() => {});
    }, [needsAiCategories]);
    useEffect(() => {
        if (!needsPrintCategories) return;
        printTemplateCategoryService
            .list()
            .then((r) => setPrintCategories((r?.data || []).filter((c) => c.status === "published")))
            .catch(() => {});
    }, [needsPrintCategories]);

    // Determine which tab is active based on current path
    const activeTab =
        menuItems.find(
            (item) =>
                location.pathname === item.path ||
                (item.matchPaths &&
                    item.matchPaths.some((p) => location.pathname.startsWith(p))) ||
                (item.dropdown &&
                    item.dropdown.some((d) =>
                        d.path === '/'
                            ? location.pathname === '/'
                            : location.pathname.startsWith(d.path)))
        )?.value || null;

    const handleTabClick = (item) => {
        if (item?.megamenu) {
            // Clicking the tab goes to the in-app tools page; hover still opens
            // the full-width megamenu panel (handled separately via showMega).
            navigate("/tools");
            return;
        }
        if (item?.external && item?.href) {
            window.open(item.href, "_blank", "noopener,noreferrer");
        } else if (item?.path) {
            navigate(item.path);
        }
    };

    // ── Fit guard ────────────────────────────────────────────────────────
    // The `navClass` breakpoints assume a standard header, but the space really available to the tab row
    // changes with the account (shop switcher, profile-completion badge, language, font…). When the row
    // would overflow (and get clipped at both ends), the lowest-priority (highest-breakpoint) "wide-screen
    // extra" tabs are hidden here and handed to the sidebar drawer (via `fitHidden`); they come back as room returns.
    const navRef = useRef(null);
    const fitWidthsRef = useRef(new Map()); // last measured width of each extra tab
    const fitHiddenRef = useRef(new Set());
    useEffect(() => { fitHiddenRef.current = fitHidden || new Set(); }, [fitHidden]);
    const navCls = (item) => cn(item.navClass, fitHidden?.has(item.path || item.value) && "!hidden");

    const menuKey = menuItems.map((it) => it.path || it.value).join("|");
    useLayoutEffect(() => {
        const nav = navRef.current;
        if (!nav || !onFitChange) return undefined;
        const extras = menuItems
            .map((item, i) => ({ i, key: item.path || item.value, min: parseInt((item.navClass || "").match(/min-\[(\d+)px\]/)?.[1], 10) }))
            .filter((x) => x.min);
        if (extras.length === 0) return undefined;
        const shown = (el) => el && el.offsetParent !== null && el.offsetWidth > 0;
        const SLACK = 12; // breathing room kept free at the row's ends

        const compute = () => {
            const cs = getComputedStyle(nav);
            const gap = parseFloat(cs.columnGap) || 0;
            const avail = nav.clientWidth - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight) - SLACK;
            const kids = [...nav.children];
            const extraIdx = new Set(extras.map((x) => x.i));
            extras.forEach(({ i, key }) => { if (shown(kids[i])) fitWidthsRef.current.set(key, kids[i].offsetWidth); });
            // Always-visible tabs (everything that is not a wide-screen extra) come first…
            const base = kids.filter((el, i) => !extraIdx.has(i) && shown(el));
            let used = base.reduce((sum, el) => sum + el.offsetWidth, 0) + gap * Math.max(0, base.length - 1);
            // …then the extras whose own breakpoint applies, lowest breakpoint (= most important) first, while they fit.
            const next = new Set();
            let fits = true;
            for (const { key, min } of [...extras].sort((a, b) => a.min - b.min)) {
                if (!window.matchMedia(`(min-width:${min}px)`).matches) continue;
                const w = fitWidthsRef.current.get(key);
                if (!w) continue; // not measured yet (first time it is shown) — leave it, the next resize pass measures it
                if (fits && used + gap + w <= avail) used += gap + w;
                else { fits = false; next.add(key); }
            }
            const cur = fitHiddenRef.current;
            if (next.size !== cur.size || [...next].some((k) => !cur.has(k))) {
                fitHiddenRef.current = next;
                onFitChange(next);
            }
        };

        compute();
        const ro = new ResizeObserver(compute);
        ro.observe(nav);
        window.addEventListener("resize", compute); // belt and braces: ResizeObserver is paused while the tab is hidden
        document.fonts?.ready?.then(compute);
        return () => { ro.disconnect(); window.removeEventListener("resize", compute); };
        // eslint-disable-next-line react-hooks/exhaustive-deps -- menuKey stands in for menuItems (a fresh array every render)
    }, [menuKey, i18n.language, onFitChange]);

    if (menuItems.length === 0) return null;

    const showMega = () => {
        clearTimeout(megaTimeoutRef.current);
        clearTimeout(aiMegaTimeoutRef.current);
        setAiMegaOpen(false);
        setMegaOpen(true);
    };
    const hideMega = () => {
        megaTimeoutRef.current = setTimeout(() => setMegaOpen(false), 120);
    };

    const closeMega = () => {
        clearTimeout(megaTimeoutRef.current);
        setMegaOpen(false);
    };

    const loadAiTemplates = () => {
        if (aiTemplatesRequestedRef.current) return;
        aiTemplatesRequestedRef.current = true;
        const cache = useAiTemplateCacheStore.getState();
        const fresh = cache.getFreshTemplates("");
        if (fresh !== undefined) { setAiTemplates(fresh); return; }
        setAiTemplatesLoading(true);
        aiTemplateService
            .list({ search: "" })
            .then((r) => {
                const tpls = r?.data || [];
                cache.setTemplates("", tpls);
                setAiTemplates(tpls);
            })
            .catch(() => { aiTemplatesRequestedRef.current = false; }) // let the next hover retry
            .finally(() => setAiTemplatesLoading(false));
    };
    const showAiMega = () => {
        clearTimeout(aiMegaTimeoutRef.current);
        clearTimeout(megaTimeoutRef.current);
        setMegaOpen(false);
        setAiMegaOpen(true);
        loadAiTemplates();
    };
    const hideAiMega = () => {
        aiMegaTimeoutRef.current = setTimeout(() => setAiMegaOpen(false), 120);
    };
    const closeAiMega = () => {
        clearTimeout(aiMegaTimeoutRef.current);
        setAiMegaOpen(false);
    };

    // Shared hover-link style for dropdown items
    const dropdownLinkClass = cn(
        "block px-3 py-1.5 text-sm rounded-md transition-colors duration-100 cursor-pointer",
        "hover:bg-zinc-100 dark:hover:bg-zinc-800"
    );

    // Find the megamenu item (if any) for the full-width panel
    const megaItem = menuItems.find((item) => item.megamenu);

    return (
        // Reserve a small horizontal padding so the center-justified tab row
        // gets a bit of breathing space from the logo (left) and the action
        // cluster (right) instead of butting right up against them. The padding
        // widens further on very large screens (≥2000px).
        <nav ref={navRef} className="relative flex items-center justify-center gap-1.5 min-[1100px]:gap-2.5 flex-1 min-w-0 overflow-x-clip px-2 min-[2000px]:px-[4vw] font-hind">
            {menuItems.map((item) => {
                const isActive = item.value === activeTab;
                const label = item.labelKey ? t(item.labelKey) : item.label;
                const badgeText = item.badge ? (i18n.language === 'bn' ? 'নতুন' : item.badge) : null;

                // ----------------------------------------------------------------
                // Category dropdown (AI templates by category)
                // ----------------------------------------------------------------
                if (item.categoryDropdown) {
                    const isPrint = item.categoryDropdown === "print";
                    const categories = isPrint ? printCategories : aiCategories;
                    const basePath = isPrint ? "/print-media" : "/ai-templates";
                    if (!isPrint) {
                        // AI templates → full-width mega panel (rendered below the nav).
                        return (
                            <div
                                key={item.value}
                                className={cn("relative inline-flex", navCls(item))}
                                onMouseEnter={showAiMega}
                                onMouseLeave={hideAiMega}
                            >
                                {badgeText && <FloatingBadge>{badgeText}</FloatingBadge>}
                                <TabButton active={isActive} onClick={() => handleTabClick(item)}>
                                    {item.icon && <span className="shrink-0">{item.icon}</span>}
                                    {label}
                                    <IconChevronDown size={12} className="shrink-0" />
                                </TabButton>
                            </div>
                        );
                    }
                    return (
                        <HoverDropdown
                            key={item.value}
                            className={navCls(item)}
                            dropdownClassName="min-w-[220px] p-1.5"
                            trigger={
                                <div className="relative inline-flex">
                                    {badgeText && <FloatingBadge>{badgeText}</FloatingBadge>}
                                    <TabButton active={isActive} onClick={() => handleTabClick(item)}>
                                        {item.icon && <span className="shrink-0">{item.icon}</span>}
                                        {label}
                                        <IconChevronDown size={12} className="shrink-0" />
                                    </TabButton>
                                </div>
                            }
                        >
                            <div className="flex flex-col gap-0.5">
                                {categories.length === 0 ? (
                                    <span className="px-3 py-2 text-sm text-zinc-400">
                                        No categories
                                    </span>
                                ) : (
                                    categories.map((cat) => (
                                        <a
                                            key={cat.id}
                                            href={`${basePath}?category=${cat.slug}`}
                                            className={cn(dropdownLinkClass, "text-zinc-900 dark:text-zinc-100 no-underline")}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                navigate(`${basePath}?category=${cat.slug}`);
                                            }}
                                        >
                                            {i18n.language === "bn" && cat.nameBn
                                                ? cat.nameBn
                                                : cat.name}
                                        </a>
                                    ))
                                )}
                            </div>
                        </HoverDropdown>
                    );
                }

                // ----------------------------------------------------------------
                // Simple dropdown (inline links array)
                // ----------------------------------------------------------------
                if (item.dropdown) {
                    return (
                        <HoverDropdown
                            key={item.value}
                            className={navCls(item)}
                            alignLeft={!item.alignRight}
                            dropdownClassName="min-w-[180px] p-1.5"
                            trigger={
                                <div className="relative inline-flex">
                                    {badgeText && <FloatingBadge>{badgeText}</FloatingBadge>}
                                    <TabButton active={isActive} onClick={() => handleTabClick(item)}>
                                        {item.icon && <span className="shrink-0">{item.icon}</span>}
                                        {label}
                                        <IconChevronDown size={12} className="shrink-0" />
                                    </TabButton>
                                </div>
                            }
                        >
                            <div className="flex flex-col gap-0.5">
                                {item.dropdown.map((link) => {
                                    const linkActive = location.pathname === link.path;
                                    return (
                                        <a
                                            key={link.path}
                                            href={link.path}
                                            className={cn(
                                                dropdownLinkClass,
                                                "text-zinc-900 dark:text-zinc-100 no-underline",
                                                linkActive && "font-semibold bg-zinc-100 dark:bg-zinc-800"
                                            )}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                navigate(link.path);
                                            }}
                                        >
                                            {link.labelKey ? t(link.labelKey) : link.label}
                                        </a>
                                    );
                                })}
                            </div>
                        </HoverDropdown>
                    );
                }

                // ----------------------------------------------------------------
                // Megamenu trigger (full-width panel rendered below nav)
                // ----------------------------------------------------------------
                if (item.megamenu) {
                    return (
                        <div
                            key={item.value}
                            className={cn("relative inline-flex", navCls(item))}
                            onMouseEnter={showMega}
                            onMouseLeave={hideMega}
                        >
                            <FloatingBadge tone="green">{i18n.language === 'bn' ? 'ফ্রি' : 'Free'}</FloatingBadge>
                            <TabButton active={isActive} onClick={() => handleTabClick(item)}>
                                {item.icon && <span className="shrink-0">{item.icon}</span>}
                                {label}
                                <IconChevronDown size={12} className="shrink-0" />
                            </TabButton>
                        </div>
                    );
                }

                // ----------------------------------------------------------------
                // Plain tab (no dropdown)
                // ----------------------------------------------------------------
                return (
                    <div key={item.value} className={cn("relative inline-flex", navCls(item))}>
                        {badgeText && <FloatingBadge>{badgeText}</FloatingBadge>}
                        <TabButton
                            active={isActive}
                            onClick={() => handleTabClick(item)}
                        >
                            {item.icon && <span className="shrink-0">{item.icon}</span>}
                            {label}
                        </TabButton>
                    </div>
                );
            })}

            {trailing}

            {/* Full-width megamenu panel — portaled into the navbar row slot so it
                spans the full container width, not just this nav's middle column. */}
            {megaItem && megaOpen && megaSlot && createPortal(
                <div
                    className={MEGA_SHELL_CLASS}
                    onMouseEnter={showMega}
                    onMouseLeave={hideMega}
                >
                    <div
                        className="grid gap-x-4 gap-y-0"
                        style={{ gridTemplateColumns: `repeat(${megaCategories.length}, minmax(0, 1fr))` }}
                    >
                        {megaCategories.map((category) => {
                            const cap = MAX_DISPLAY_BY_CAT[category.id] || MAX_DISPLAY;
                            const visible = category.tools.slice(0, cap);
                            const remaining = category.tools.length - cap;
                            const isBn = i18n.language === "bn";

                            return (
                                <div key={category.id} className="flex flex-col">
                                    {/* Category header */}
                                    <Link
                                        to={`/tools?tab=${category.id}`}
                                        onClick={closeMega}
                                        className="flex items-center gap-3 no-underline transition-opacity hover:opacity-90"
                                        style={{
                                            backgroundColor: isDark ? `${category.color}18` : category.bgColor,
                                            border: `1.5px solid ${category.color}${isDark ? "40" : "25"}`,
                                            borderRadius: "12px",
                                            padding: "10px 12px",
                                            marginBottom: "14px",
                                        }}
                                    >
                                        <span
                                            className="shrink-0 flex items-center justify-center"
                                            style={{
                                                width: 34,
                                                height: 34,
                                                borderRadius: 9,
                                                backgroundColor: isDark ? "#27272a" : "white",
                                                boxShadow: `0 2px 8px ${category.color}30`,
                                            }}
                                        >
                                            <img
                                                src={toolIconSrc(category.iconImg)}
                                                alt={category.title}
                                                className="w-5 h-5 object-contain"
                                                onError={(e) => {
                                                    // TEMP: the education (result) category icon is missing on the
                                                    // CDN — fall back to a bundled SVG until it's uploaded.
                                                    if (category.id === 'result' && !e.currentTarget.dataset.fallback) {
                                                        e.currentTarget.dataset.fallback = '1';
                                                        e.currentTarget.src = '/tools-icon-fallback/education.svg';
                                                    }
                                                }}
                                            />
                                        </span>
                                        <div className="min-w-0">
                                            <div style={{ fontWeight: 700, fontSize: 14, color: isDark ? "#f4f4f5" : "#111", lineHeight: 1.3 }}>
                                                {isBn ? category.titleBn : category.title}
                                            </div>
                                            <div style={{ fontSize: 11, fontWeight: 500, color: category.color, marginTop: 2 }}>
                                                {isBn ? fmtBn(category.tools.length) : category.tools.length} {isBn ? "টুলস" : "tools"}
                                            </div>
                                        </div>
                                    </Link>

                                    {/* Tool links */}
                                    <div className="flex flex-col flex-1">
                                        {visible.map((tool) => {
                                            const slug = toSlug(tool.path);
                                            const internal = isInternalTool({ slug });
                                            // Some tools have no CDN icon — fall back to a Tabler vector
                                            // (mirrors the ICON_OVERRIDES map used by the /tools grid).
                                            const OverrideIcon = ICON_OVERRIDES[slug] || MENU_RECOLOR_ICONS[slug];
                                            const linkClassName = "flex items-center gap-3 px-2 py-2 rounded-lg no-underline transition-colors duration-100 hover:bg-zinc-100 dark:hover:bg-zinc-800";
                                            const linkContent = (
                                                <>
                                                    {OverrideIcon ? (
                                                        // The vector fallback takes its category's own colour, so a
                                                        // tool without a CDN icon still sits in its column's hue.
                                                        <OverrideIcon
                                                            stroke={1.6}
                                                            className="w-9 h-9 shrink-0 p-1.5 rounded-lg"
                                                            style={{
                                                                color: category.color,
                                                                backgroundColor: isDark ? `${category.color}22` : category.bgColor,
                                                            }}
                                                        />
                                                    ) : (
                                                        <img
                                                            src={toolIconSrc(tool.iconImg)}
                                                            alt={tool.name}
                                                            className="w-9 h-9 shrink-0 object-contain"
                                                        />
                                                    )}
                                                    <span className="min-w-0">
                                                        <span className="block text-sm font-semibold text-zinc-800 dark:text-zinc-200 leading-snug">
                                                            {isBn ? tool.nameBn : tool.name}
                                                        </span>
                                                        <span className="block text-xs text-zinc-400 dark:text-zinc-500 leading-tight truncate">
                                                            {tool.name}
                                                        </span>
                                                    </span>
                                                </>
                                            );

                                            return internal ? (
                                                <Link
                                                    key={tool.path}
                                                    to={tool.path}
                                                    onClick={closeMega}
                                                    className={linkClassName}
                                                >
                                                    {linkContent}
                                                </Link>
                                            ) : (
                                                <a
                                                    key={tool.path}
                                                    href={SITE_URL + tool.path}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    onClick={closeMega}
                                                    className={linkClassName}
                                                >
                                                    {linkContent}
                                                </a>
                                            );
                                        })}

                                        {remaining > 0 && (
                                            <Link
                                                to={`/tools?tab=${category.id}`}
                                                onClick={closeMega}
                                                className="flex items-center gap-1 px-2 py-2 text-sm font-semibold no-underline transition-opacity hover:opacity-70"
                                                style={{ color: category.color }}
                                            >
                                                +{isBn ? fmtBn(remaining) : remaining} + {isBn ? "আরো" : "more"} &rsaquo;
                                            </Link>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* Footer */}
                    <div className="mt-4 pt-3 border-t border-zinc-100 dark:border-zinc-800 flex justify-center">
                        <Link
                            to="/tools"
                            onClick={closeMega}
                            className="text-sm font-medium no-underline"
                            style={{
                                background: megamenuData.meta.brandColors.gradient,
                                WebkitBackgroundClip: "text",
                                WebkitTextFillColor: "transparent",
                                backgroundClip: "text",
                            }}
                        >
                            {i18n.language === "bn" ? "সকল টুলস দেখুন" : "View all tools"} &rsaquo;
                        </Link>
                    </div>
                </div>,
                megaSlot
            )}

            {/* AI-templates mega panel — same slot as the Tools megamenu, but a narrower shell. */}
            {aiMegaOpen && megaSlot && createPortal(
                <div className={AI_SHELL_CLASS} onMouseEnter={showAiMega} onMouseLeave={hideAiMega}>
                    <AiTemplateMegaMenu
                        categories={aiCategories}
                        templates={aiTemplates}
                        loading={aiTemplatesLoading}
                        isDark={isDark}
                        onClose={closeAiMega}
                    />
                </div>,
                megaSlot
            )}
        </nav>
    );
}
